import { useCallback, useEffect, useState } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import UploadField from "@/components/UploadField";
import { toast } from "sonner";
import { Pin, PinOff, Megaphone, CheckCircle2, CalendarClock, Paperclip } from "lucide-react";

const EMOJIS = ["🙏", "❤️", "🌱", "👏", "🔥"];

export default function CircleExtras({ circleId, isOwner, pinnedReload = 0 }) {
  const t = useT();
  const [pinned, setPinned] = useState([]);
  const [anns, setAnns] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", requires_confirmation: false, scheduled_for: "", attachment_url: "", attachment_kind: "none" });

  const loadPinned = useCallback(async () => {
    try { const { data } = await api.get(`/circles/${circleId}/pinned`); setPinned(data?.messages || []); }
    catch { /* silent */ }
  }, [circleId]);
  const loadAnns = useCallback(async () => {
    try { const { data } = await api.get(`/circles/${circleId}/announcements`); setAnns(Array.isArray(data) ? data : []); }
    catch (err) { toast.error(apiError(err)); }
  }, [circleId]);

  useEffect(() => { loadPinned(); loadAnns(); }, [loadPinned, loadAnns]);
  useEffect(() => { loadPinned(); }, [pinnedReload, loadPinned]);

  const unpin = async (mid) => {
    try { await api.post(`/circles/${circleId}/messages/${mid}/pin`); await loadPinned(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const post = async () => {
    if (form.title.trim().length < 2) { toast.error(t("cext.err_title")); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        reaction_kind: "emoji",
        requires_confirmation: form.requires_confirmation,
        attachment_kind: form.attachment_url ? form.attachment_kind : "none",
        attachment_url: form.attachment_url || null,
      };
      if (form.scheduled_for) payload.scheduled_for = new Date(form.scheduled_for).toISOString();
      await api.post(`/circles/${circleId}/announcements`, payload);
      toast.success(t("cext.published"));
      setForm({ title: "", body: "", requires_confirmation: false, scheduled_for: "", attachment_url: "", attachment_kind: "none" });
      setOpen(false);
      await loadAnns();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const react = async (aid, emoji) => {
    try { await api.post(`/circles/announcements/${aid}/react`, { emoji }); await loadAnns(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const confirm = async (aid) => {
    try { await api.post(`/circles/announcements/${aid}/confirm`); await loadAnns(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="border-t border-border">
      {/* Pinned */}
      {pinned.length > 0 && (
        <div className="border-b border-border p-4" data-testid="circle-pinned-box">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Pin className="h-3.5 w-3.5" /> {t("cext.pinned")}</p>
          <div className="space-y-2">
            {pinned.map((m) => (
              <div key={m.id} className="flex items-start justify-between gap-2 rounded-xl bg-secondary/60 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground">{m.from_name}</p>
                  <p className="text-sm text-foreground">{m.body}</p>
                </div>
                {isOwner && (
                  <button type="button" onClick={() => unpin(m.id)} data-testid={`unpin-${m.id}`} className="shrink-0 text-muted-foreground hover:text-foreground" title={t("cext.unpin")}>
                    <PinOff className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Announcements */}
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><Megaphone className="h-4 w-4" /> {t("cext.announcements")}</p>
          {isOwner && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="circle-post-announcement"><Megaphone className="h-3.5 w-3.5" /> {t("cext.new")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("cext.post_title")}</DialogTitle>
                  <DialogDescription>{t("cext.post_desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="ann-title">{t("cext.title")}</Label>
                    <Input id="ann-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t("cext.title")} data-testid="ann-title" />
                  </div>
                  <div>
                    <Label htmlFor="ann-body">{t("cext.text")}</Label>
                    <Textarea id="ann-body" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder={t("cext.body_ph")} data-testid="ann-body" />
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                    <span className="text-sm text-foreground">{t("cext.require_confirm")}</span>
                    <Switch checked={form.requires_confirmation} onCheckedChange={(v) => setForm((f) => ({ ...f, requires_confirmation: v }))} data-testid="ann-confirm-toggle" />
                  </div>
                  <div>
                    <Label htmlFor="ann-schedule" className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> {t("cext.schedule")}</Label>
                    <Input id="ann-schedule" type="datetime-local" value={form.scheduled_for} onChange={(e) => setForm((f) => ({ ...f, scheduled_for: e.target.value }))} data-testid="ann-schedule-pick" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> {t("cext.attachment")}</Label>
                    <UploadField value={form.attachment_url} onChange={(p) => setForm((f) => ({ ...f, attachment_url: p, attachment_kind: "image" }))} kind="photo" accept="image/*" testId="ann-attach-upload" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)} data-testid="ann-cancel">{t("cir.cancel")}</Button>
                  <Button onClick={post} disabled={busy || form.title.trim().length < 2} data-testid="ann-submit">{t("cext.post")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {anns.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">{t("cext.empty")}</p>
        ) : (
          <div className="space-y-3">
            {anns.map((a) => (
              <div key={a.id} data-testid={`announcement-${a.id}`} className="rounded-xl border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-foreground">{a.title}</p>
                  {a.status === "scheduled" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{t("cext.scheduled")}</span>}
                </div>
                {a.body && <p className="mt-1 text-sm text-muted-foreground">{a.body}</p>}
                {a.attachment_url && a.attachment_kind === "image" && (
                  <img src={fileUrl(a.attachment_url)} alt="" className="mt-2 max-h-48 rounded-lg object-cover" />
                )}
                <p className="mt-1 text-[11px] text-muted-foreground">{a.coach_name} · {shortDate(a.published_at || a.created_at)}</p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {EMOJIS.map((em) => (
                    <button key={em} type="button" onClick={() => react(a.id, em)} data-testid={`ann-reaction-${a.id}`} className={`rounded-full px-2 py-0.5 text-sm transition-colors ${a.my_reaction === em ? "bg-primary/10 ring-1 ring-primary" : "bg-secondary hover:bg-accent"}`}>
                      {em}{a.reactions_summary?.[em] ? ` ${a.reactions_summary[em]}` : ""}
                    </button>
                  ))}
                  {a.requires_confirmation && (
                    <Button
                      size="sm"
                      variant={a.confirmed_by_me ? "secondary" : "outline"}
                      className="ml-auto gap-1.5"
                      onClick={() => confirm(a.id)}
                      disabled={a.confirmed_by_me}
                      data-testid={`ann-confirm-${a.id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> {a.confirmed_by_me ? t("cext.confirmed") : t("cext.read")} {a.confirmed_count ? `(${a.confirmed_count})` : ""}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
